import { useEffect, useState, useMemo } from 'react';
import { SecureStorage } from '../storage/SecureStorage';
import { API_BASE } from '../config';
import api from '../api/Client';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from "lucide-react";
import styles from "../styles/UserPage.module.css";



type StoredUser = {
  id: number;
  fullName: string;
  email: string;
  phone?: string;
};

type CustomerApi = {
  profile_photo?: string | null;
  name?: string;
  email?: string;
  phone?: string;
};

export default function UserPage() {
    const [user, setUser] = useState<StoredUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [balance, setBalance] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Imagen de perfil: obtener profile_photo desde la API
    const defaultProfileImg = "data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%22120%22%20height%3D%22120%22%20viewBox%3D%220%200%20120%20120%22%3E%3Crect%20width%3D%22120%22%20height%3D%22120%22%20fill%3D%22%23f3f4f6%22/%3E%3Ctext%20x%3D%2250%25%22%20y%3D%2250%25%22%20dominant-baseline%3D%22middle%22%20text-anchor%3D%22middle%22%20fill%3D%22%239ca3af%22%20font-family%3D%22Arial%2C%20sans-serif%22%20font-size%3D%2212%22%3ENo%20Image%3C/text%3E%3C/svg%3E";
    const [imgSrc, setImgSrc] = useState<string>(defaultProfileImg);

    const navigate = useNavigate();

    useEffect(() => {
      (async () => {
        setLoading(true);
        try {
          const currentUser: StoredUser | null = await SecureStorage.getUser?.() ?? null;
          if (!currentUser) {
            setError("No se pudo cargar el usuario. Inicia sesión de nuevo.");
            setLoading(false);
            return;
          }
          setUser(currentUser);

          // Obtener datos completos del usuario para la foto
          const resApi = await api.get<{ data: CustomerApi }>(`/api/customers/get/${currentUser.id}`);
          const customer = resApi.data?.data;
          let photoUrl = defaultProfileImg;
          if (customer?.profile_photo) {
            photoUrl = `${API_BASE}/api/profile_pic/${customer.profile_photo}`;
          }
          setImgSrc(photoUrl);

          // Obtener balance
          const res = await api.get<{ balance: number }>(`/api/mobile/user/e-wallet-balance`, {
            params: { user_id: currentUser.id }
          });
          setBalance(res.data?.balance ?? 0);
        } catch (e: any) {
          setError(e?.message || 'Error cargando usuario');
        } finally {
          setLoading(false);
        }
      })();
    }, []);

    return (
      <div className={styles.userPage}>
        {/* Cabecera */}
        <div className={styles.profileHeader}>
          <img
            src={imgSrc}
            alt="Foto perfil"
            onError={() => setImgSrc(defaultProfileImg)}
          />
          <div className={styles.userName}>{user?.fullName}</div>
          <div className={styles.userEmail}>{user?.email}</div>
          {user?.phone && <div className={styles.userPhone}>{user.phone}</div>}
          <div className={styles.balance}>Saldo: {balance ?? 0} €</div>
          <div className={styles.buttons}>
            <button className={styles.btnPrimary} onClick={() => navigate("/profile")}>Mi perfil</button>
            <button className={styles.btnSecondary} onClick={() => navigate("/profile/edit")}>Editar perfil</button>
          </div>
        </div>

        {/* Opciones */}
        <div className={styles.options}>
          {[
            { title: "Configuración", path: "/settings" },
            { title: "Mis favoritos", path: "/favorites", dataTour: "user-favoritos" },
            { title: "Documento", path: "/documents", dataTour: "user-documentos" },
            { title: "Ver tarjetas", path: "/payment-methods", dataTour: "user-mis-tarjetas" },
            { title: "Monedero virtual", path: "/ewallet" },
            { title: "Añadir cupón", path: "/add-coupons" },
            { title: "Comunidad Move", path: "/move" }
          ].map((item) => (
            <div
              key={item.title}
              className={styles.option}
              onClick={() => navigate(item.path)}
              {...(item.dataTour ? { 'data-tour': item.dataTour } : {})}
            >
              <span>{item.title}</span>
              <ChevronRight size={20} color="#888" />
            </div>
          ))}
          <div className={styles.techCredit}>Desarollado por Nexo Tech Labs</div>
        </div>
      </div>
    );
}
